import { useMemo, useState } from "react";
import { Calculator, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

type Goal = "brand" | "performance" | "rekrytering" | "thought";
type Channel = "linkedin" | "youtube" | "tiktok" | "instagram" | "webb" | "tv";

const goals: { id: Goal; label: string; help: string }[] = [
  { id: "brand", label: "Bygga varumärke", help: "Längre format, storytelling, känsla" },
  { id: "performance", label: "Driva sälj / leads", help: "Kortare hooks, tydlig CTA, A/B" },
  { id: "rekrytering", label: "Rekrytering & employer", help: "Medarbetar­berättelser & kultur" },
  { id: "thought", label: "Thought leadership", help: "Intervjuer, perspektiv, expert­positionering" },
];

const channels: { id: Channel; label: string }[] = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "youtube", label: "YouTube" },
  { id: "tiktok", label: "TikTok" },
  { id: "instagram", label: "Instagram" },
  { id: "webb", label: "Webb / landningssidor" },
  { id: "tv", label: "Linjär / streaming" },
];

interface Recommendation {
  tier: "Pulse Lite" | "Pulse Core" | "Pulse Pro" | "Prime";
  priceLabel: string;
  filmsPerMonth: string;
  rationale: string;
  href: string;
}

function recommend(goal: Goal, channelCount: number, postsPerMonth: number): Recommendation {
  const need = postsPerMonth * (channelCount >= 3 ? 1.4 : 1);
  if (goal === "brand" && channelCount <= 2 && postsPerMonth <= 4) {
    return { tier: "Prime", priceLabel: "från 250 000 kr / produktion", filmsPerMonth: "1 stort projekt", rationale: "Få men kraftfulla brand-filmer ger mer än många små. Börja med Prime och komplettera med Pulse senare.", href: "/prime" };
  }
  if (need <= 2) return { tier: "Pulse Lite", priceLabel: "14 995 kr / mån", filmsPerMonth: "2 klipp / mån", rationale: "Stadig rytm för att hålla närvaron levande — utan att producera mer än ni hinner publicera.", href: "/priser" };
  if (need <= 6) return { tier: "Pulse Core", priceLabel: "34 995 kr / mån", filmsPerMonth: "3–4 klipp / mån", rationale: "Vår mest populära nivå — räcker för flera kanaler med format­variation och rätt distribution.", href: "/priser" };
  return { tier: "Pulse Pro", priceLabel: "45 995 kr / mån", filmsPerMonth: "6+ klipp / mån", rationale: "När takt och strategisk höjd ska kombineras — workshops, manus och kreativ ledning ingår.", href: "/priser" };
}

export function RoiCalculator() {
  const [goal, setGoal] = useState<Goal>("performance");
  const [selectedChannels, setSelectedChannels] = useState<Channel[]>(["linkedin", "youtube"]);
  const [postsPerMonth, setPostsPerMonth] = useState(4);

  const rec = useMemo(
    () => recommend(goal, selectedChannels.length, postsPerMonth),
    [goal, selectedChannels.length, postsPerMonth],
  );

  const toggleChannel = (c: Channel) =>
    setSelectedChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  return (
    <div className="overflow-hidden rounded-3xl border border-mint/20 bg-gradient-to-br from-surface via-surface to-mint/[0.04]">
      <div className="grid gap-0 md:grid-cols-[1.1fr_1fr]">
        {/* Inputs */}
        <div className="border-b border-white/10 p-8 md:border-b-0 md:border-r md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
            <Calculator className="h-3.5 w-3.5" /> Interaktiv
          </div>
          <h3 className="mt-3 text-2xl font-semibold md:text-3xl">Hur mycket film behöver ni?</h3>
          <p className="mt-2 text-sm text-muted-foreground">Tre snabba val — vi pekar ut rätt nivå.</p>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">1 · Mål</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {goals.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGoal(g.id)}
                  className={cn(
                    "rounded-xl border p-3 text-left text-sm transition-all",
                    goal === g.id ? "border-mint bg-mint/10 text-foreground" : "border-white/10 text-foreground/85 hover:border-white/25",
                  )}
                >
                  <div className="font-semibold">{g.label}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{g.help}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">2 · Kanaler ni publicerar på</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {channels.map((c) => {
                const active = selectedChannels.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.id)}
                    className={cn(
                      "rounded-full border px-3.5 py-1.5 text-xs transition-all",
                      active ? "border-mint bg-mint text-mint-foreground" : "border-white/15 text-foreground/80 hover:border-white/35",
                    )}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-7">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground">
              <span>3 · Önskade publiceringar / månad</span>
              <span className="text-mint">{postsPerMonth}</span>
            </div>
            <input
              type="range"
              min={1}
              max={12}
              value={postsPerMonth}
              onChange={(e) => setPostsPerMonth(Number(e.target.value))}
              className="mt-3 w-full accent-mint"
            />
          </div>
        </div>

        {/* Result */}
        <div className="p-8 md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
            <Sparkles className="h-3.5 w-3.5" /> Rekommendation
          </div>
          <div className="mt-3 text-3xl font-semibold leading-tight md:text-4xl">{rec.tier}</div>
          <div className="mt-2 text-base text-mint">{rec.priceLabel}</div>
          <div className="mt-1 text-sm text-muted-foreground">{rec.filmsPerMonth}</div>

          <p className="mt-6 text-sm leading-relaxed text-foreground/85">{rec.rationale}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={rec.href}
              onClick={() => track("roi_calc_complete", { tier: rec.tier, goal, channel_count: selectedChannels.length, posts: postsPerMonth })}
              className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-medium text-mint-foreground transition-transform hover:-translate-y-0.5"
            >
              Läs mer om {rec.tier} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/kontakt"
              onClick={() => track("cta_click", { cta_id: "book_strategy_meeting", location: "roi_calc" })}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm transition-colors hover:border-white/35"
            >
              Boka strategimöte
            </Link>
          </div>

          <p className="mt-6 text-xs text-muted-foreground">
            Rekommendationen är en utgångspunkt. På strategimötet justerar vi efter er verkliga kanalmix, kampanjkalender och målsättningar.
          </p>
        </div>
      </div>
    </div>
  );
}
